import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

import WidgetHeader from '../Widget/WidgetHeader';
import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPartner from '../Widget/OverlayPartner';
import NextAction from '../Widget/NextAction';

import CheckIcon from '../../components/Svg/CheckIcon';
import ShareDrawer from '../../components/ShareDrawer';
import { isLongText } from '../../components/Shared/commonFunctions';
import { TransitionContainerNoExit } from '../../components/Transition/TranstionContainer';

import { formatEmoji } from '../../utils/fillDynamicText';
import { copyFor } from '../../copy/copy';

import { Container } from '../DefaultStyles/Details';
import {
    Body,
    Content,
    Details,
    DetailsContainer,
    Footer,
    Title as QuestionText
} from '../DefaultStyles/Shared';

const AnswersWrapper = styled.div`
    width: 100%;
`;

const AnswerBar = styled.div`
    text-align: center;
    border-radius: 4px;
    margin: 0 8px 15px 8px;
    padding: 0 8px;
    float: left;
    position: relative;
    height: ${props =>
        props.isMobileResolution
            ? props.noOfAnswers === 4
                ? '46px'
                : '50px'
            : props.noOfAnswers === 4
            ? '48px'
            : '88px'};
    width: ${props =>
        props.noOfAnswers === 4
            ? 'calc(50% - 16px)'
            : props =>
                  props.noOfAnswers === 3 ? 'calc(33.3% - 16px)' : 'calc(50% - 16px)'};
    border: ${props =>
        props.isCorrect
            ? 'solid 4px #ff245e'
            : props => (props.answerAnswered ? 'solid 4px #0b2947' : 'solid 1px #ccc')};
    color: ${props => (props.isCorrect ? '#ff245e' : '#000')};
    opacity: ${props => (props.isCorrect || props.answerAnswered ? '1' : '0.4')};

    @media (max-width: 30em) {
        width: 100%;
        margin-bottom: 14px;
        height: ${props =>
            props.isMobileResolution
                ? props =>
                      props.noOfAnswers === 4
                          ? '46px'
                          : props.noOfAnswers === 2
                          ? '70px'
                          : '62px'
                : props.noOfAnswers === 4
                ? '56px'
                : '88px'};
    }
`;

const IconWrapper = styled.div`
    width: 24px;
    height: 24px;
    position: absolute;
    left: calc(50% - 12px);
    bottom: -16px;
    border-radius: 100%;
    background-color: #ff245e;
    line-height: 24px;
`;

const CloseIcon = styled.div`
    width: 22px;
    height: 38px;
    transform: rotate(45deg);
    font-family: Averta;
    font-size: 24px;
    line-height: 100%;
    text-align: left;
    color: #fff;
`;

const IconWrapperAnswered = styled.div`
    width: 24px;
    height: 24px;
    position: absolute;
    left: calc(50% - 12px);
    bottom: -16px;
    border-radius: 100%;
    background-color: #0b2947;
    line-height: 24px;
`;

const PercentageBar = styled.div`
    font-size: 8px;
    font-weight: bold;
    line-height: 2.2;
    letter-spacing: 2px;
    text-align: center;
    position: relative;
    transform: translateY(-50%);
    top: ${props => (props.noOfAnswers === 4 ? '46%' : '48.5%')};

    @media (max-width: 30em) {
        top: ${props => (props.isCorrect || props.answerAnswered ? '43%' : '50%')};
    }
`;

const PercentageText = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: ${props =>
        props.isLongText && !props.noOfAnswers === 4
            ? '13px'
            : props => (props.noOfAnswers === 4 ? '14px' : '16px')};
    font-weight: 600;
    line-height: 0.92;
    letter-spacing: 0;
    text-transform: ${props => (props.makeUppercase ? 'uppercase' : 'none')};

    @media (min-width: 30em) {
        margin-top: -3px;
    }

    .emoji {
        font-size: 22px;
    }
`;

const PercentageLabel = styled.span``;

const QuizSuccess = props => (
    <TransitionContainerNoExit zIndex={15}>
        <Container>
            <Overlay />
            <OverlayPublisher />
            <OverlayPartner transitionType="right-to-left" />
            <Body>
                <WidgetHeader />
                <Content>
                    <QuestionText
                        isShortText={!isLongText(props.feedbackText, 28)}
                        isLongText={isLongText(props.feedbackText, 60)}
                        isExtraLongText={isLongText(props.feedbackText, 95)}
                    >
                        {props.answeredCorrect
                            ? copyFor('feedback', 'correct', 'quiz')
                            : copyFor('feedback', 'incorrect', 'quiz')}
                        {props.feedbackText}
                    </QuestionText>
                    <DetailsContainer>
                        <Details>
                            <AnswersWrapper>
                                {props.results.map(r => (
                                    <AnswerBar
                                        key={r.id}
                                        isCorrect={r.isCorrect}
                                        answerAnswered={r.answerAnswered}
                                        noOfAnswers={props.noOfAnswers}
                                        isMobileResolution={props.isMobileResolution}
                                    >
                                        <PercentageBar
                                            noOfAnswers={props.noOfAnswers}
                                            isCorrect={r.isCorrect}
                                            answerAnswered={r.answerAnswered}
                                        >
                                            {!props.isMobileResolution && (
                                                <PercentageLabel>
                                                    {copyFor(
                                                        'feedback',
                                                        'percentAnswered',
                                                        'quiz',
                                                        undefined,
                                                        { percentage: r.percentage }
                                                    )}{' '}
                                                    <br />
                                                </PercentageLabel>
                                            )}
                                            <PercentageText
                                                isLongText={isLongText(r.text)}
                                                makeUppercase={
                                                    props.makeUppercase.length ===
                                                    props.noOfAnswers
                                                }
                                                noOfAnswers={props.noOfAnswers}
                                                dangerouslySetInnerHTML={{
                                                    __html: formatEmoji(r.text)
                                                }}
                                            />
                                        </PercentageBar>
                                        {r.isCorrect && (
                                            <IconWrapper>
                                                <CheckIcon />
                                            </IconWrapper>
                                        )}
                                        {r.answerAnswered && !r.isCorrect && (
                                            <IconWrapperAnswered>
                                                <CloseIcon>+</CloseIcon>
                                            </IconWrapperAnswered>
                                        )}
                                    </AnswerBar>
                                ))}
                            </AnswersWrapper>
                        </Details>
                    </DetailsContainer>
                </Content>
            </Body>
            <Footer>
                <ShareDrawer newsletterSignUpVisible={true} />
            </Footer>
        </Container>
        <NextAction />
    </TransitionContainerNoExit>
);

QuizSuccess.propTypes = {
    results: PropTypes.array.isRequired,
    makeUppercase: PropTypes.array.isRequired,
    feedbackText: PropTypes.string,
    answeredCorrect: PropTypes.bool.isRequired,
    noOfAnswers: PropTypes.number,
    isMobileResolution: PropTypes.bool
};

export default connect(
    state => {
        return {
            isMobileResolution: state.widget.isMobileResolution,
            feedbackText: state.quiz.successInfo.feedbackText,
            noOfAnswers: state.quiz != null ? state.quiz.results.length : 0,
            answeredCorrect:
                state.quiz != null
                    ? state.quiz.results.find(r => r.isCorrect === true).id ===
                      state.quiz.lastAnsweredQuiz.answerId
                    : false,
            makeUppercase:
                state.quiz != null
                    ? state.quiz.results.filter(function(a) {
                          return a.text.length <= 8;
                      })
                    : [],
            results:
                state.quiz != null
                    ? state.quiz.results
                          .map(a => ({
                              ...a,
                              percentage:
                                  state.quiz.quizAnswers !== undefined
                                      ? state.quiz.quizAnswers.find(
                                            r => r.answerId === a.id
                                        ) !== undefined
                                          ? state.quiz.quizAnswers.find(
                                                r => r.answerId === a.id
                                            ).percentage
                                          : 0
                                      : 0,
                              isCorrect: a.isCorrect,
                              answerAnswered:
                                  a.id === state.quiz.lastAnsweredQuiz.answerId
                          }))
                          .slice(0, 4)
                    : []
        };
    },
    {}
)(QuizSuccess);



// WEBPACK FOOTER //
// ./src/containers/Quiz/QuizSuccess.jsx